/* =============================================================
   TLPC — Wishlist store (localStorage)
   File: assets/wishlist-tlpc.js

   Loaded once from sections/header-tlpc.liquid — the header renders on
   every page and owns the count bubble, so this is the one place it
   belongs. Nothing else needs to load it.

   Shopify has no native wishlist. This is browser-local: the list lives
   in localStorage, per device, per browser. It is not tied to the
   customer account and does not sync across devices.

   Public API — window.TLPCWishlist
     .get()          -> array of product handles, newest first
     .has(handle)    -> boolean
     .add(handle)    -> boolean, true when the list changed
     .remove(handle) -> boolean, true when the list changed
     .toggle(handle) -> boolean, true when the product is now saved
     .clear()        -> void
     .count()        -> number
     .sync(root)     -> repaint toggles/counters inside root (default: document)

   Event — fired on document
     'tlpc:wishlist:change'
     detail: { items, handle, action }   action: add | remove | clear | sync

   Markup contract
     toggle  <button data-tlpc-wishlist data-product-handle="foo">
     counter <span data-tlpc-wishlist-count></span>
   ============================================================= */

(function () {
  'use strict';

  var KEY = 'tlpc:wishlist';
  var EVENT = 'tlpc:wishlist:change';
  var MAX = 200;

  var items = [];
  var writable = true;

  /* ---------- storage ---------- */

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return [];
      var list = JSON.parse(raw);
      if (!(list instanceof Array)) return [];
      var out = [];
      for (var i = 0; i < list.length; i++) {
        if (typeof list[i] === 'string' && list[i] && out.indexOf(list[i]) === -1) {
          out.push(list[i]);
        }
      }
      return out.slice(0, MAX);
    } catch (e) {
      return [];
    }
  }

  function write() {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(items));
      writable = true;
    } catch (e) {
      /* private mode or quota — the list still works for this page view */
      writable = false;
    }
  }

  items = read();

  /* ---------- dom painting ---------- */

  function paintToggles(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-tlpc-wishlist]');

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var handle = el.getAttribute('data-product-handle');
      var on = !!handle && items.indexOf(handle) !== -1;

      if (el.classList.contains('is-active') !== on) {
        el.classList.toggle('is-active', on);
      }
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  }

  function paintCounts() {
    var n = items.length;
    var text = n > 99 ? '99+' : String(n);
    var nodes = document.querySelectorAll('[data-tlpc-wishlist-count]');

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var value = el.querySelector('[data-tlpc-wishlist-count-value]') || el;
      if (value.textContent !== text) value.textContent = text;
      if (n === 0) {
        el.setAttribute('hidden', 'hidden');
      } else {
        el.removeAttribute('hidden');
      }
    }
  }

  function emit(handle, action) {
    var detail = { items: items.slice(), handle: handle || null, action: action || 'sync' };
    var evt;

    try {
      evt = new CustomEvent(EVENT, { detail: detail, bubbles: true });
    } catch (e) {
      evt = document.createEvent('CustomEvent');
      evt.initCustomEvent(EVENT, true, false, detail);
    }

    document.dispatchEvent(evt);
  }

  function refresh(handle, action) {
    paintToggles();
    paintCounts();
    emit(handle, action);
  }

  /* ---------- api ---------- */

  var API = {
    key: KEY,
    event: EVENT,

    get: function () {
      return items.slice();
    },

    count: function () {
      return items.length;
    },

    has: function (handle) {
      return !!handle && items.indexOf(handle) !== -1;
    },

    add: function (handle) {
      if (!handle || items.indexOf(handle) !== -1) return false;
      items.unshift(handle);
      if (items.length > MAX) items.length = MAX;
      write();
      refresh(handle, 'add');
      return true;
    },

    remove: function (handle) {
      var i = handle ? items.indexOf(handle) : -1;
      if (i === -1) return false;
      items.splice(i, 1);
      write();
      refresh(handle, 'remove');
      return true;
    },

    toggle: function (handle) {
      if (!handle) return false;
      if (items.indexOf(handle) === -1) {
        API.add(handle);
        return true;
      }
      API.remove(handle);
      return false;
    },

    clear: function () {
      if (!items.length) return;
      items = [];
      write();
      refresh(null, 'clear');
    },

    persistent: function () {
      return writable;
    },

    sync: function (root) {
      paintToggles(root);
      paintCounts();
    }
  };

  window.TLPCWishlist = API;

  /* ---------- toggle clicks (delegated, so late cards work too) ---------- */

  function closestToggle(node) {
    while (node && node !== document) {
      if (node.nodeType === 1 && node.hasAttribute && node.hasAttribute('data-tlpc-wishlist')) {
        return node;
      }
      node = node.parentNode;
    }
    return null;
  }

  document.addEventListener('click', function (event) {
    var btn = closestToggle(event.target);
    if (!btn) return;

    event.preventDefault();
    event.stopPropagation();

    var handle = btn.getAttribute('data-product-handle');
    if (!handle) return;

    API.toggle(handle);

    btn.classList.remove('is-pop');
    /* force reflow so the pop replays on a rapid second click */
    void btn.offsetWidth;
    btn.classList.add('is-pop');
  });

  /* ---------- keep tabs in step ---------- */

  window.addEventListener('storage', function (event) {
    if (event.key && event.key !== KEY) return;
    items = read();
    paintToggles();
    paintCounts();
    emit(null, 'sync');
  });

  /* ---------- paint cards that arrive later (ajax grids, section render) ---------- */

  function watch() {
    if (!('MutationObserver' in window)) return;

    var queued = false;

    var observer = new MutationObserver(function (records) {
      if (queued) return;

      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes && records[i].addedNodes.length) {
          queued = true;
          window.requestAnimationFrame(function () {
            queued = false;
            paintToggles();
            paintCounts();
          });
          return;
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    paintToggles();
    paintCounts();
    watch();
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();